import React from "react";
import { Box, FormControl, FormLabel, Textarea, Option } from "@mui/joy";
import DarkSelect from "../../common/DarkSelect";
import DarkDatePicker from "../../common/DarkDatePicker";
import DarkTypography from "../../common/DarkTypography";
import DarkTimePicker, {
  DARK_TIME_SLOTS,
  nextDarkTimeSlotAfter,
} from "../../common/DarkTimePicker";
import DarkCheckbox from "../../common/DarkCheckbox";
import CommonModal from "../../common/CommonModal";
import { useLeave } from "../../../contexts/api";
import { useAlert } from "../../../contexts/app/notificationToast";
import { useAuth } from "../../../contexts/api";
import { useHRData } from "../../../contexts/app/hrData";
import type {
  CreateLeaveRequest,
  LeaveType,
  UserLeaveTypeBalance,
} from "../../../api/leave/Leave.typed";
import { isLeaveTypeHourlyUnit } from "../../../api/leave/Leave.typed";
import {
  calculateWorkingDaysFromYmdInTimeZone,
  getTodayDateStringInTimeZone,
  wallDateTimeToIsoUtc,
  ymdToRFC3339UTC,
} from "../../../utils/dateTime";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import useAppSettings from "../../../contexts/app/appSettings";
import {
  colorTokens,
  ctaAccentColor,
  darkDividerLineSx,
  hrCalloutWarningSx,
  hrPanelMutedSx,
  hrTextareaChromeSx,
} from "../../../styles/globalStyles";
import { findGlobalUnavailabilityLeaveType } from "../../../utils/planningLeaveHelpers";

function withGlobalIndispoNoCompanyPayload(
  payload: CreateLeaveRequest,
  typeId: number,
  catalogLeaveTypes: LeaveType[],
): CreateLeaveRequest {
  const g = findGlobalUnavailabilityLeaveType(catalogLeaveTypes);
  if (g != null && typeId === g.ID) {
    return { ...payload, CompanyID: null };
  }
  return payload;
}

const leaveModalSectionDividerSx = {
  ...darkDividerLineSx,
  border: 0,
  flexShrink: 0,
  my: { xs: 0.35, sm: 0.5 },
} as const;

const FULL_DAY_START_HM = "00:00";
const FULL_DAY_END_HM = "23:59";

function slotMinutes(hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  return h * 60 + m;
}

function defaultHourlyTimes(): { startHm: string; endHm: string } {
  const d = new Date();
  const nowM = d.getHours() * 60 + d.getMinutes();
  const slots = [...DARK_TIME_SLOTS];
  let startHm = slots[0];
  for (const t of slots) {
    if (slotMinutes(t) <= nowM) {
      startHm = t;
    }
  }
  const i = slots.indexOf(startHm);
  let endHm = slots[Math.min(i + 2, slots.length - 1)];
  if (endHm <= startHm) {
    endHm = nextDarkTimeSlotAfter(startHm);
  }
  return { startHm, endHm };
}

interface LeaveRequestFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  allowedTypeIds?: number[];
  /** When set, only these types are listed (no union with balance types). */
  restrictTypeSelectionToAllowedOnly?: boolean;
  /** YYYY-MM-DD; pre-fills day / range when opening (e.g. planning calendar). */
  initialDateKey?: string | null;
}

export default function LeaveRequestFormModal({
  open,
  onClose,
  onSave,
  allowedTypeIds,
  restrictTypeSelectionToAllowedOnly = false,
  initialDateKey = null,
}: LeaveRequestFormModalProps) {
  const { createLeaveRequest, listBalancesByUser } = useLeave();
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const { leaveTypes: allLeaveTypes } = useHRData();
  const { config } = useAppSettings();
  const companyTimeZone = config?.TimeZone;
  const modal = useConfirmationModal();

  const [formData, setFormData] = React.useState<CreateLeaveRequest>({
    TypeID: 0,
    StartDate: "",
    EndDate: "",
    Reason: "",
  });

  const [typeBalance, setTypeBalance] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [calculatedDays, setCalculatedDays] = React.useState<number>(0);
  const [leaveTypes, setLeaveTypes] = React.useState<LeaveType[]>([]);
  const [userBalances, setUserBalances] = React.useState<UserLeaveTypeBalance[]>([]);
  const [hourlyStartHm, setHourlyStartHm] = React.useState(defaultHourlyTimes().startHm);
  const [hourlyEndHm, setHourlyEndHm] = React.useState(defaultHourlyTimes().endHm);
  const [hourlyFullDay, setHourlyFullDay] = React.useState(true);
  const [reasonIsPublic, setReasonIsPublic] = React.useState(true);

  React.useEffect(() => {
    if (open && user?.id) {
      const load = async () => {
        const paged = await listBalancesByUser(user.id, 1, 1000);
        const balances = paged?.data || [];
        setUserBalances(balances);
        const typeIdsFromBalances = Array.from(new Set(balances.map((b) => b.TypeID)));
        const typesById = new Map(allLeaveTypes.map((t) => [t.ID, t]));

        let associated: LeaveType[];

        if (restrictTypeSelectionToAllowedOnly && allowedTypeIds?.length) {
          associated = allowedTypeIds
            .map((id) => typesById.get(id))
            .filter((t): t is LeaveType => !!t && t.IsActive);
        } else {
          const allowedIds = allowedTypeIds?.length ? allowedTypeIds : [];
          const associatedTypeIds = Array.from(new Set([...typeIdsFromBalances, ...allowedIds]));
          associated = associatedTypeIds
            .map((id) => typesById.get(id))
            .filter((t): t is LeaveType => !!t && t.IsActive);
        }

        const globalIndispo = findGlobalUnavailabilityLeaveType(allLeaveTypes);
        if (globalIndispo?.IsActive && !associated.some((t) => t.ID === globalIndispo.ID)) {
          associated = [...associated, globalIndispo];
        }

        setLeaveTypes(associated);
        setFormData((prev) => ({
          ...prev,
          TypeID: associated.length > 0 ? associated[0].ID : 0,
        }));
      };
      void load();
    }
  }, [
    open,
    user?.id,
    listBalancesByUser,
    allowedTypeIds,
    allLeaveTypes,
    restrictTypeSelectionToAllowedOnly,
  ]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const key = initialDateKey?.trim();
    const t = defaultHourlyTimes();
    if (key && /^\d{4}-\d{2}-\d{2}$/.test(key)) {
      setFormData((prev) => ({
        ...prev,
        StartDate: key,
        EndDate: key,
      }));
      setHourlyStartHm(t.startHm);
      setHourlyEndHm(t.endHm);
      setHourlyFullDay(true);
    } else {
      const dateStr = getTodayDateStringInTimeZone(companyTimeZone);
      setFormData((prev) => ({
        ...prev,
        StartDate: dateStr,
        EndDate: dateStr,
      }));
      setHourlyStartHm(t.startHm);
      setHourlyEndHm(t.endHm);
      setHourlyFullDay(true);
    }
    setReasonIsPublic(true);
  }, [open, initialDateKey, companyTimeZone]);

  React.useEffect(() => {
    if (!open) {
      setFormData({
        TypeID: 0,
        StartDate: "",
        EndDate: "",
        Reason: "",
      });
      setCalculatedDays(0);
      const ht = defaultHourlyTimes();
      const dateStr = getTodayDateStringInTimeZone(companyTimeZone);
      setFormData((prev) => ({
        ...prev,
        StartDate: dateStr,
        EndDate: dateStr,
      }));
      setHourlyStartHm(ht.startHm);
      setHourlyEndHm(ht.endHm);
      setHourlyFullDay(true);
      setReasonIsPublic(true);
    }
  }, [open, companyTimeZone]);

  // Recompute working days when start/end date change (day-based leave only; hourly types use Unit from leave type)
  React.useEffect(() => {
    const selected = leaveTypes.find((t) => t.ID === formData.TypeID);
    if (isLeaveTypeHourlyUnit(selected?.Unit)) {
      setCalculatedDays(0);
      return;
    }
    if (formData.StartDate && formData.EndDate) {
      if (formData.StartDate <= formData.EndDate) {
        const days = calculateWorkingDaysFromYmdInTimeZone(
          formData.StartDate,
          formData.EndDate,
          companyTimeZone,
        );
        setCalculatedDays(days);
      } else {
        setCalculatedDays(0);
      }
    } else {
      setCalculatedDays(0);
    }
  }, [formData.StartDate, formData.EndDate, formData.TypeID, leaveTypes, companyTimeZone]);

  // Load balance for selected type when it uses a counter
  React.useEffect(() => {
    const fetchTypeBalance = async () => {
      const selected = leaveTypes.find((t) => t.ID === formData.TypeID);
      if (!open || !selected || !selected.UsesCounter || !user?.id) {
        setTypeBalance(null);
        return;
      }
      const mine = userBalances.find((b: UserLeaveTypeBalance) => b.TypeID === formData.TypeID);
      setTypeBalance(mine ? mine.Balance : 0);
    };
    fetchTypeBalance();
  }, [open, formData.TypeID, leaveTypes, userBalances, user?.id]);

  const handleInputChange = (field: keyof CreateLeaveRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /** Keeps end day ≥ start day; default end = start when start moves forward or end was empty. */
  const handleLeaveStartDateChange = (value: string) => {
    setFormData((prev) => {
      const next = { ...prev, StartDate: value };
      if (!prev.EndDate || prev.EndDate < value) {
        next.EndDate = value;
      }
      return next;
    });
  };

  const performSubmit = React.useCallback(async () => {
    const selectedType = leaveTypes.find((t) => t.ID === formData.TypeID);

    if (isLeaveTypeHourlyUnit(selectedType?.Unit)) {
      if (!selectedType) {
        showAlert("Type de congé invalide", "warning");
        return;
      }
      if (!formData.StartDate || !formData.EndDate) {
        showAlert("Dates manquantes", "warning");
        return;
      }
      if (new Date(formData.StartDate) > new Date(formData.EndDate)) {
        showAlert("La date de fin doit être après ou égale à la date de début", "warning");
        return;
      }
      const startHm = hourlyFullDay ? FULL_DAY_START_HM : hourlyStartHm;
      const endHm = hourlyFullDay ? FULL_DAY_END_HM : hourlyEndHm;
      const startIso = wallDateTimeToIsoUtc(formData.StartDate, startHm);
      const endIso = wallDateTimeToIsoUtc(formData.EndDate, endHm);
      if (!startIso || !endIso) {
        showAlert("Dates ou horaires invalides", "warning");
        return;
      }
      const startMs = Date.parse(startIso);
      const endMs = Date.parse(endIso);
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        showAlert("La fin du créneau doit être après le début (dates et heures)", "warning");
        return;
      }
      if (
        selectedType.UsesCounter &&
        !selectedType.AllowsNegative &&
        getBalanceAfterRequest() < 0
      ) {
        showAlert("Solde insuffisant: négatif non autorisé pour ce type", "warning");
        return;
      }
      const rangeLabel =
        formData.StartDate === formData.EndDate
          ? formData.StartDate
          : `${formData.StartDate} → ${formData.EndDate}`;
      const ok = await modal.showConfirmation(
        "Créer la demande ?",
        `${selectedType.Name || `Type #${formData.TypeID}`} • ${rangeLabel}`,
      );
      if (!ok) return;
      setLoading(true);
      const cleanedData: CreateLeaveRequest = {
        TypeID: Number(formData.TypeID),
        StartDate: ymdToRFC3339UTC(formData.StartDate),
        EndDate: ymdToRFC3339UTC(formData.EndDate),
        StartAt: startIso,
        EndAt: endIso,
        ReasonIsPublic: reasonIsPublic,
      };
      if (formData.Reason && formData.Reason.trim() !== "") {
        cleanedData.Reason = formData.Reason.trim();
      }
      try {
        await createLeaveRequest(
          withGlobalIndispoNoCompanyPayload(cleanedData, formData.TypeID, allLeaveTypes),
        );
        onSave?.();
        onClose();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur lors de la création de la demande";
        const friendly = msg.includes("User context not found")
          ? "Session expirée, veuillez vous reconnecter."
          : msg;
        showAlert(friendly, "danger");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!formData.StartDate || !formData.EndDate) {
      showAlert("Dates manquantes", "warning");
      return;
    }
    if (new Date(formData.StartDate) > new Date(formData.EndDate)) {
      showAlert("Dates incorrectes", "warning");
      return;
    }
    if (calculatedDays === 0) {
      showAlert("Aucun jour ouvrable", "warning");
      return;
    }
    if (selectedType?.UsesCounter && !selectedType.AllowsNegative && getBalanceAfterRequest() < 0) {
      showAlert("Solde insuffisant: négatif non autorisé pour ce type", "warning");
      return;
    }
    const ok = await modal.showConfirmation(
      "Créer la demande de congé ?",
      `${selectedType?.Name || `Type #${formData.TypeID}`} • ${calculatedDays}j`,
    );
    if (!ok) return;
    setLoading(true);
    const cleanedData: CreateLeaveRequest = {
      TypeID: Number(formData.TypeID),
      StartDate: ymdToRFC3339UTC(formData.StartDate),
      EndDate: ymdToRFC3339UTC(formData.EndDate),
    };
    if (formData.Reason && formData.Reason.trim() !== "") {
      cleanedData.Reason = formData.Reason.trim();
    }
    try {
      await createLeaveRequest(
        withGlobalIndispoNoCompanyPayload(cleanedData, formData.TypeID, allLeaveTypes),
      );
      onSave?.();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur lors de la création de la demande";
      const friendly = msg.includes("User context not found")
        ? "Session expirée, veuillez vous reconnecter."
        : msg;
      showAlert(friendly, "danger");
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    calculatedDays,
    leaveTypes,
    allLeaveTypes,
    typeBalance,
    modal,
    createLeaveRequest,
    onSave,
    onClose,
    showAlert,
    hourlyStartHm,
    hourlyEndHm,
    hourlyFullDay,
    reasonIsPublic,
    companyTimeZone,
  ]);

  const selectedLeaveType = React.useMemo(
    () => leaveTypes.find((t) => t.ID === formData.TypeID),
    [leaveTypes, formData.TypeID],
  );
  const isHourlyLeave = isLeaveTypeHourlyUnit(selectedLeaveType?.Unit);

  const getBalanceAfterRequest = () => {
    if (!selectedLeaveType?.UsesCounter || typeBalance == null) return 0;
    return typeBalance - calculatedDays;
  };

  const shouldShowBalanceWarning = () => {
    return !!selectedLeaveType?.UsesCounter && getBalanceAfterRequest() < 0;
  };

  const submitDisabled = (() => {
    if (!formData.TypeID || loading) return true;
    if (isLeaveTypeHourlyUnit(selectedLeaveType?.Unit)) {
      if (!formData.StartDate || !formData.EndDate) return true;
      if (new Date(formData.StartDate) > new Date(formData.EndDate)) return true;
      if (
        selectedLeaveType?.UsesCounter &&
        !selectedLeaveType?.AllowsNegative &&
        getBalanceAfterRequest() < 0
      ) {
        return true;
      }
      return false;
    }
    return (
      !formData.StartDate ||
      !formData.EndDate ||
      calculatedDays === 0 ||
      (!!selectedLeaveType?.UsesCounter &&
        !selectedLeaveType?.AllowsNegative &&
        getBalanceAfterRequest() < 0)
    );
  })();

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Demande de congés"
      onConfirm={performSubmit}
      confirmLabel="Créer la demande"
      submitDisabled={submitDisabled}
      loading={loading}
      size="lg"
    >
      {selectedLeaveType?.UsesCounter && (typeBalance !== null || calculatedDays > 0) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 0.75, sm: 1.5 },
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 1,
            py: { xs: 0.75, sm: 0.75 },
            px: { xs: 1, sm: 1 },
            ...hrPanelMutedSx,
          }}
        >
          <DarkTypography level="body-xs" colorVariant="primary">
            Solde:{" "}
            <DarkTypography component="span" colorVariant="title" sx={{ fontWeight: "bold" }}>
              {typeBalance ?? 0}j
            </DarkTypography>
          </DarkTypography>
          {calculatedDays > 0 && (
            <DarkTypography level="body-xs" colorVariant="primary">
              Après:{" "}
              <DarkTypography
                component="span"
                sx={{
                  fontWeight: "bold",
                  color:
                    getBalanceAfterRequest() >= 0
                      ? colorTokens.status.success
                      : colorTokens.status.danger,
                }}
              >
                {getBalanceAfterRequest()}j
              </DarkTypography>
            </DarkTypography>
          )}
        </Box>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          performSubmit();
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 1.25 } }}>
          <FormControl required>
            <FormLabel
              sx={{ color: ctaAccentColor, fontSize: { xs: "0.75rem", sm: "0.8125rem" }, mb: 0.25 }}
            >
              Type
            </FormLabel>
            <DarkSelect
              value={formData.TypeID}
              onChange={(_, value) => handleInputChange("TypeID", Number(value))}
              placeholder="Sélectionner"
            >
              {leaveTypes.map((type) => (
                <Option key={type.ID} value={type.ID}>
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                    {type.Name}
                  </Box>
                </Option>
              ))}
            </DarkSelect>
          </FormControl>

          <Box component="hr" role="separator" sx={leaveModalSectionDividerSx} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1, sm: 1.15 },
              p: { xs: 0.75, sm: 1 },
              borderRadius: "8px",
              maxWidth: "95vw",
              ...hrPanelMutedSx,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.25, sm: 0.35 } }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.75, sm: 1.25 },
                }}
              >
                <DarkDatePicker
                  label="Jour de début"
                  labelAccent
                  required
                  formControlProps={{ sx: { flex: 1 } }}
                  value={formData.StartDate}
                  onChange={handleLeaveStartDateChange}
                />
                <DarkDatePicker
                  label="Jour de fin"
                  labelAccent
                  required
                  formControlProps={{ sx: { flex: 1 } }}
                  value={formData.EndDate}
                  onChange={(v) => handleInputChange("EndDate", v)}
                  min={formData.StartDate}
                />
              </Box>
            </Box>

            {isHourlyLeave && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 1 } }}>
                {!hourlyFullDay && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: { xs: 0.75, sm: 1 },
                    }}
                  >
                    <DarkTimePicker
                      label="Heure début"
                      labelAccent
                      mode="start"
                      formControlProps={{ sx: { flex: 1, minWidth: 0 } }}
                      value={hourlyStartHm}
                      onChange={setHourlyStartHm}
                    />
                    <DarkTimePicker
                      label="Heure fin"
                      labelAccent
                      mode="end"
                      startTime={hourlyStartHm}
                      formControlProps={{ sx: { flex: 1, minWidth: 0 } }}
                      value={hourlyEndHm}
                      onChange={setHourlyEndHm}
                    />
                  </Box>
                )}
                <DarkCheckbox
                  checked={hourlyFullDay}
                  onChange={(e) => setHourlyFullDay(e.target.checked)}
                  label="Toute la journée"
                />
              </Box>
            )}

            {!isHourlyLeave && calculatedDays > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  pt: { xs: 0.35, sm: 0.5 },
                  borderTop: `1px solid ${colorTokens.border.default}`,
                }}
              >
                <DarkTypography level="body-xs" colorVariant="muted">
                  Durée
                </DarkTypography>
                <DarkTypography level="body-xs" colorVariant="accent" sx={{ fontWeight: "bold" }}>
                  {calculatedDays}j
                </DarkTypography>
              </Box>
            )}

            {!isHourlyLeave && shouldShowBalanceWarning() && (
              <Box sx={{ ...hrCalloutWarningSx, p: { xs: 0.5, sm: 0.65 } }}>
                <DarkTypography level="body-xs" sx={{ color: colorTokens.status.warning }}>
                  ⚠️ Découvert
                </DarkTypography>
              </Box>
            )}
          </Box>

          <Box component="hr" role="separator" sx={leaveModalSectionDividerSx} />

          <FormControl>
            <FormLabel
              sx={{ color: ctaAccentColor, fontSize: { xs: "0.75rem", sm: "0.8125rem" }, mb: 0.25 }}
            >
              Motif
            </FormLabel>
            <Textarea
              minRows={1}
              placeholder="Motif..."
              value={formData.Reason || ""}
              onChange={(e) => handleInputChange("Reason", e.target.value)}
              sx={hrTextareaChromeSx}
            />
          </FormControl>

          {isHourlyLeave && (
            <>
              <Box component="hr" role="separator" sx={leaveModalSectionDividerSx} />
              <DarkCheckbox
                checked={reasonIsPublic}
                onChange={(e) => setReasonIsPublic(e.target.checked)}
                label="Motif visible sur le planning"
              />
            </>
          )}
        </Box>
      </form>
    </CommonModal>
  );
}
